import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Settings, MousePointer, Smartphone, BarChart2 } from 'lucide-react';
import { useInteractionMode } from '@/contexts/InteractionModeContext';
import { useGameState } from '@/contexts/GameStateContext';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

import { DeleteElementModal } from './DeleteElementModal';

import { Element as ElementType } from '@/types';

// Declare global type for the window object
declare global {
  interface Window {
    selectElementForDeletion?: (element: ElementType) => void;
    handleDeleteElementClick?: () => void;
  }
}

const SettingsMenu: React.FC = () => {
  const { mode, setMode } = useInteractionMode();
  const { discoveries, goals, points, stats } = useGameState();

  const [statsOpen, setStatsOpen] = useState(false);
  const [deleteElementOpen, setDeleteElementOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementType | null>(null);

  // Create a function to handle the delete element action
  const handleDeleteElement = () => {
    setDeleteElementOpen(true);

    // Create a "Delete Element Mode" similar to the reporting mode
    document.body.classList.add('delete-element-mode');

    // Set up a global handler for element selection
    window.selectElementForDeletion = (element: ElementType) => {
      setSelectedElement(element);
      document.body.classList.remove('delete-element-mode');
    };

    // Add a click handler to the document to capture clicks outside of elements
    const handleDocumentClick = (e: MouseEvent) => {
      // If the click is not on an element, cancel the delete mode
      const target = e.target as HTMLElement;
      if (!target.closest('.element')) {
        setDeleteElementOpen(false);
        document.body.classList.remove('delete-element-mode');
        document.removeEventListener('click', handleDocumentClick);
      }
    };

    // Add the event listener
    setTimeout(() => {
      document.addEventListener('click', handleDocumentClick);
    }, 100);
  };

  // Expose the handleDeleteElement function globally
  React.useEffect(() => {
    window.handleDeleteElementClick = handleDeleteElement;

    return () => {
      delete window.handleDeleteElementClick;
    };
  }, []);

  // Handle closing the delete element modal
  const handleCloseDeleteModal = () => {
    setDeleteElementOpen(false);
    setSelectedElement(null);
    document.body.classList.remove('delete-element-mode');
  };

  // Get stats from the context with fallbacks
  const {
    totalDiscoveries = discoveries.length,
    totalForged = 0,
    itemsForged = 0,
    locationsForged = 0,
    charactersForged = 0,
    goalsCompleted = goals.filter((goal) => goal.matched).length,
    skipsEarned = 0,
    skipsUsed = 0,
    keysCollected = 0,
    keysUsed = 0,
    deconstructions = 0,
  } = stats || {};

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Settings">
            <Settings className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Settings</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground px-2 py-1.5">
              Interaction Mode
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => setMode('drag-and-drop')}
              className={mode === 'drag-and-drop' ? 'bg-accent' : ''}
            >
              <MousePointer className="mr-2 h-4 w-4" />
              <span>Drag and Drop</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setMode('tap-to-location')}
              className={mode === 'tap-to-location' ? 'bg-accent' : ''}
            >
              <Smartphone className="mr-2 h-4 w-4" />
              <span>Tap Mode</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => setStatsOpen(true)}>
              <BarChart2 className="mr-2 h-4 w-4" />
              <span>View Stats</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Stats Dialog */}
      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your VibeCraft Stats</DialogTitle>
            <DialogDescription>Track your progress and achievements</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Elements Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Elements</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{totalDiscoveries}</span>
                  <span className="text-xs text-muted-foreground">Discoveries</span>
                </div>
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{totalForged}</span>
                  <span className="text-xs text-muted-foreground">Total Forged</span>
                </div>
              </div>
            </div>

            {/* Forge Types Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Forge Types</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-xl font-bold text-primary">{itemsForged}</span>
                  <span className="text-xs text-muted-foreground">Items</span>
                </div>
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-xl font-bold text-primary">{locationsForged}</span>
                  <span className="text-xs text-muted-foreground">Locations</span>
                </div>
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-xl font-bold text-primary">{charactersForged}</span>
                  <span className="text-xs text-muted-foreground">Characters</span>
                </div>
              </div>
            </div>

            {/* Goals Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Goals</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{goalsCompleted}</span>
                  <span className="text-xs text-muted-foreground">Completed</span>
                </div>
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{points}</span>
                  <span className="text-xs text-muted-foreground">Total Score</span>
                </div>
              </div>
            </div>

            {/* Skips Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Skips</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{skipsEarned || 0}</span>
                  <span className="text-xs text-muted-foreground">Earned</span>
                </div>
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{skipsUsed || 0}</span>
                  <span className="text-xs text-muted-foreground">Used</span>
                </div>
              </div>
            </div>

            {/* Keys Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Keys</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{keysCollected || 0}</span>
                  <span className="text-xs text-muted-foreground">Earned</span>
                </div>
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{keysUsed || 0}</span>
                  <span className="text-xs text-muted-foreground">Used</span>
                </div>
              </div>
            </div>

            {/* Deconstructions Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Deconstructions</h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-card border rounded-lg p-3 flex flex-col items-center">
                  <span className="text-2xl font-bold text-primary">{deconstructions || 0}</span>
                  <span className="text-xs text-muted-foreground">Total</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Element Dialog */}
      {deleteElementOpen && (
        <>
          {/* Instruction banner at the top of the screen */}
          {!selectedElement && (
            <div className="fixed top-0 left-0 right-0 bg-destructive text-destructive-foreground py-3 px-4 z-50 flex items-center justify-between shadow-lg">
              <div className="flex items-center">
                <span className="font-medium">
                  Delete Element Mode: Click on any element in the Elements & Discoveries panel to
                  delete it.
                </span>
              </div>
              <Button variant="secondary" onClick={handleCloseDeleteModal} className="ml-4">
                Cancel
              </Button>
            </div>
          )}

          {/* Semi-transparent overlay for everything except Elements & Discoveries */}
          {!selectedElement && (
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 pointer-events-none">
              {/* This div is transparent and covers the entire screen */}
            </div>
          )}
        </>
      )}

      {/* Delete Element Modal */}
      <DeleteElementModal
        isOpen={!!selectedElement && deleteElementOpen}
        onClose={handleCloseDeleteModal}
        element={selectedElement}
      />
    </>
  );
};

export default SettingsMenu;
